import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash, Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { itApi } from './itApi';
import { todayJst } from '@/utils/jstDate';

export const DISPOSAL_REASONS = [
  { value: 'broken', label: '故障' },
  { value: 'obsolete', label: '陳腐化' },
  { value: 'lost', label: '紛失' },
  { value: 'stolen', label: '盗難' },
  { value: 'expired', label: '期限切れ' },
  { value: 'other', label: 'その他' },
];
export const DISPOSAL_METHODS = [
  { value: 'recycle', label: 'リサイクル' },
  { value: 'trash', label: '廃棄' },
  { value: 'sold', label: '売却' },
  { value: 'donated', label: '譲渡' },
];

const NONE = '__none__';

/**
 * 廃棄 (dispose) confirmation dialog — captures reason/method/date/qty and calls the backend.
 * Consumables can be partially scrapped via the `qty` field; durable assets always fully dispose.
 */
export default function DisposalDialog({ open, asset, onClose, onDisposed }) {
  const [form, setForm] = useState({
    disposal_date: todayJst(),
    disposal_reason: 'broken',
    disposal_method: '',
    disposal_notes: '',
    qty: 1,
  });
  const [saving, setSaving] = useState(false);

  const isConsumable = (asset?.asset_type || 'durable') === 'consumable';
  const currentQty = asset?.on_hand_qty ?? 0;

  useEffect(() => {
    if (open) {
      setForm({
        disposal_date: todayJst(),
        disposal_reason: 'broken',
        disposal_method: '',
        disposal_notes: '',
        qty: isConsumable ? Math.min(1, currentQty || 1) : undefined,
      });
    }
  }, [open, asset]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!asset) return null;

  const submit = async () => {
    if (isConsumable) {
      const q = Number(form.qty || 0);
      if (!(q > 0)) { toast.error('廃棄数量を入力してください'); return; }
      if (q > currentQty) { toast.error(`在庫（${currentQty}）を超える数量は廃棄できません`); return; }
    }
    setSaving(true);
    try {
      const body = {
        disposal_date: form.disposal_date || null,
        disposal_reason: form.disposal_reason,
        disposal_method: form.disposal_method || null,
        disposal_notes: form.disposal_notes || null,
        qty: isConsumable ? Number(form.qty) : null,
      };
      const res = await itApi.disposeAsset(asset.id, body);
      toast.success(isConsumable ? `${form.qty} ${asset.unit || ''} を廃棄しました` : '資産を廃棄しました');
      onDisposed?.(res);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '廃棄処理に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="disposal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash size={22} weight="duotone" className="text-rose-600" />
            廃棄処理 · {asset.name}
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2 text-xs text-amber-800">
          <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
          <div>
            {isConsumable
              ? `現在の在庫: ${currentQty} ${asset.unit || ''}。指定した数量分だけ廃棄します。`
              : '廃棄すると資産は「廃棄」状態になり、割り当てられたIPは解放されます。'}
          </div>
        </div>

        <div className="space-y-3 mt-2">
          {isConsumable && (
            <div>
              <Label className="text-xs font-semibold text-zinc-600">廃棄数量 ＊</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number" min="0.001" step="any"
                  value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })}
                  className="h-10 tabular-nums" data-testid="dispose-qty"
                />
                <span className="text-sm text-zinc-500">{asset.unit || '個'}</span>
                <Button
                  type="button" variant="outline" size="sm"
                  className="h-10 whitespace-nowrap"
                  onClick={() => setForm({ ...form, qty: currentQty })}
                  data-testid="dispose-qty-all"
                >
                  全数量
                </Button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">廃棄日 ＊</Label>
              <Input type="date" value={form.disposal_date}
                     onChange={(e) => setForm({ ...form, disposal_date: e.target.value })}
                     className="h-10 mt-1" data-testid="dispose-date" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">理由 ＊</Label>
              <Select value={form.disposal_reason} onValueChange={(v) => setForm({ ...form, disposal_reason: v })}>
                <SelectTrigger className="h-10 mt-1" data-testid="dispose-reason"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DISPOSAL_REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-600">方法</Label>
            <Select value={form.disposal_method || NONE} onValueChange={(v) => setForm({ ...form, disposal_method: v === NONE ? '' : v })}>
              <SelectTrigger className="h-10 mt-1" data-testid="dispose-method"><SelectValue placeholder="選択..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>—</SelectItem>
                {DISPOSAL_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-600">備考</Label>
            <Textarea value={form.disposal_notes}
                      onChange={(e) => setForm({ ...form, disposal_notes: e.target.value })}
                      rows={2} className="mt-1" data-testid="dispose-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>キャンセル</Button>
          <Button onClick={submit} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="dispose-submit">
            <Trash size={14} weight="bold" className="mr-1.5" />
            {saving ? '処理中...' : '廃棄する'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
